import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const CEREMONY_PIN = "1234";

export const CreateBusinessPartner = ({ onSubmit }) => {
  const [requirePin, setRequirePin] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);
  const [pinValue, setPinValue] = useState("");
  const [pinError, setPinError] = useState("");
  const [unlocked, setUnlocked] = useState(false);

  const [formData, setFormData] = useState({
    nombre: "",
    correo: "",
    empresa: "",
    celular: "",
  });

  const [submitting, setSubmitting] = useState(false);

  // Detectar view en querystring
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("view") === "login") {
      setRequirePin(true);
      setPinOpen(true); // abre modal al cargar
    }
  }, []);

  const confirmPin = () => {
    if (pinValue.trim() !== CEREMONY_PIN) {
      setPinError("PIN incorrecto");
      return;
    }
    setPinOpen(false);
    setUnlocked(true);
  };

  const onPinKey = (e) => {
    if (e.key === "Enter") confirmPin();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const payload = {
        name: (formData.nombre || "").trim(),
        email: (formData.correo || "").trim() || null,
        company: (formData.empresa || "").trim() || null,
        phone: (formData.celular || "").trim() || null,
      };

      const res = await fetch("https://middleware-lite.hitch.cl/api/business-partners", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json", // evita redirects 302 de validación
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        // intenta leer el detalle de error del backend
        let detail = "";
        try {
          const errJson = await res.json();
          if (errJson?.errors) {
            const flat = Object.values(errJson.errors).flat();
            detail = `: ${flat.join(", ")}`;
          } else if (errJson?.message) {
            detail = `: ${errJson.message}`;
          }
        } catch { }
        throw new Error(`HTTP ${res.status}${detail}`);
      }

      const data = await res.json();
      alert(`✅ Socio de negocio creado con éxito${data?.id ? ` (ID: ${data.id})` : ""}`);
      setFormData({ nombre: "", correo: "", empresa: "", celular: "" });
    } catch (err) {
      console.error(err);
      alert(`❌ Error al crear socio de negocio: ${err.message || "desconocido"}`);
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <section className="relative w-full py-10">
      {/* ---- MODAL PIN ---- */}
      <AnimatePresence>
        {pinOpen && requirePin && (
          <motion.div
            className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="w-full max-w-sm mx-4 rounded-2xl border border-white/10 bg-gradient-to-b from-gray-900 to-black p-6"
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
            >
              <h3 className="text-lg font-semibold text-white mb-2">
                PIN requerido
              </h3>
              <p className="text-sm text-white/60 mb-4">
                Ingresa el PIN para continuar.
              </p>
              <input
                type="password"
                inputMode="numeric"
                pattern="\d*"
                maxLength={8}
                autoFocus
                value={pinValue}
                onChange={(e) => {
                  setPinValue(e.target.value);
                  setPinError("");
                }}
                onKeyDown={onPinKey}
                className="w-full px-4 py-2 rounded-lg bg-black border border-white/10 text-white outline-none focus:border-red-500"
                placeholder="••••"
              />
              {pinError && (
                <div className="mt-2 text-sm text-hitch">{pinError}</div>
              )}

              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => {
                    setPinOpen(false);
                    setUnlocked(false);
                  }}
                  className="px-3 py-2 text-sm rounded-md bg-gray-800 border border-white/10 text-white hover:bg-gray-700"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmPin}
                  className="px-4 py-2 text-sm rounded-md bg-hitch text-white hover:bg-red-500"
                >
                  Confirmar
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ---- OPCIÓN DESBLOQUEAR ---- */}
      {!pinOpen && requirePin && !unlocked && (
        <div className="text-center mt-10">
          <button
            onClick={() => setPinOpen(true)}
            className="px-4 py-2 rounded-lg bg-hitch text-white font-semibold hover:bg-red-500"
          >
            🔒 Ingresar PIN
          </button>
        </div>
      )}

      {/* ---- FORMULARIO ---- */}
      {(!requirePin || unlocked) && (
        <form
          onSubmit={handleSubmit}
          className="max-w-md mx-auto bg-gradient-to-b from-slate-900/80 to-black/80 rounded-2xl p-6 border border-white/10 shadow"
        >
          <h2 className="text-xl font-bold text-white mb-4">
            Crear Socio de Negocio
          </h2>

          <label className="block mb-3">
            <span className="text-sm text-white/70">Nombre*</span>
            <input
              type="text"
              required
              value={formData.nombre}
              onChange={(e) =>
                setFormData({ ...formData, nombre: e.target.value })
              }
              className="mt-1 w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white outline-none focus:border-red-500"
            />
          </label>

          <label className="block mb-3">
            <span className="text-sm text-white/70">Correo*</span>
            <input
              type="email"
              required
              value={formData.correo}
              onChange={(e) =>
                setFormData({ ...formData, correo: e.target.value })
              }
              className="mt-1 w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white outline-none focus:border-red-500"
            />
          </label>

          <label className="block mb-3">
            <span className="text-sm text-white/70">Empresa*</span>
            <input
              type="text"
              required
              value={formData.empresa}
              onChange={(e) =>
                setFormData({ ...formData, empresa: e.target.value })
              }
              className="mt-1 w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white outline-none focus:border-red-500"
            />
          </label>

          <label className="block mb-4">
            <span className="text-sm text-white/70">Celular (opcional)</span>
            <input
              type="tel"
              value={formData.celular}
              onChange={(e) =>
                setFormData({ ...formData, celular: e.target.value })
              }
              className="mt-1 w-full px-3 py-2 rounded-md bg-black/40 border border-white/10 text-white outline-none focus:border-red-500"
            />
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-2 px-4 rounded-lg bg-hitch text-white font-semibold hover:bg-red-500 disabled:opacity-50"
          >
            {submitting ? "Creando..." : "Crear Socio"}
          </button>
        </form>
      )}
    </section>
  );
};
